import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { db } from "../../lib/firebase";
import { collection, getDocs, query, where, doc, getDoc, updateDoc, deleteDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useAuthContext } from "../../contexts/AuthContext";
import { useAdminGuard } from "../../hooks/usePermissionGuard";
import { ArrowLeft, BookOpen, Users, DollarSign, TrendingUp, Calendar, Award, Eye, CheckCircle, Edit, Ban, Star, Trash2, AlertTriangle, UserX, Activity, HardDrive, Clock, GraduationCap } from "lucide-react";

interface CourseStats {
  id: string;
  courseId?: string;
  title: string;
  thumbnail: string;
  price: number;
  totalStudents: number;
  totalLessons: number;
  revenue: number;
  createdAt: Date | null;
}

interface StudentInfo {
  userId: string;
  name: string;
  email: string;
  courseName: string;
  courseId: string;
  amountPaid: number;
  progress: number;
  lastActivity: Date | null;
  storageUsed: number;
  enrolledAt: Date | null;
  banned?: boolean;
}

export default function InstructorDetail() {
  const { instructorId } = useParams<{ instructorId: string }>();
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [instructor, setInstructor] = useState<any>(null);
  const [courses, setCourses] = useState<CourseStats[]>([]);
  const [students, setStudents] = useState<StudentInfo[]>([]);
  const [totalStats, setTotalStats] = useState({
    totalCourses: 0,
    totalStudents: 0,
    totalRevenue: 0,
    totalLessons: 0
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<StudentInfo | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    // Check if user is admin
    if (user?.email !== "viitamint@gmail.com" && user?.role !== "admin") {
      navigate("/not-allowed");
      return;
    }
    loadInstructorDetail();
  }, [user, navigate, instructorId]);

  async function loadInstructorDetail() {
    if (!instructorId) return;

    try {
      // Load instructor info
      const instructorDoc = await getDoc(doc(db, "users", instructorId));
      if (instructorDoc.exists()) {
        setInstructor({ uid: instructorId, ...instructorDoc.data() });
      }

      // Load instructor's courses
      const coursesQuery = query(
        collection(db, "courses"),
        where("instructorId", "==", instructorId)
      );
      const coursesSnap = await getDocs(coursesQuery);
      const instructorCourses = coursesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Load all progress
      const progressSnap = await getDocs(collection(db, "progress"));
      const allProgress = progressSnap.docs.map(doc => doc.data());

      // Load all payments for revenue calculation - only instructor-approved
      const paymentsSnap = await getDocs(collection(db, "payments"));
      const allPayments = paymentsSnap.docs
        .filter(doc => doc.data().status === 'completed' && doc.data().approvedBy)
        .map(doc => ({ id: doc.id, ...doc.data() }));

      // Calculate stats for each course
      const courseStatsList: CourseStats[] = [];
      let totalRevenue = 0;
      let totalStudents = 0;
      let totalLessons = 0;

      for (const course of instructorCourses) {
        const courseData = course as any;
        
        // Count unique students for this course
        const courseStudents = new Set(
          allProgress
            .filter((p: any) => p.courseId === courseData.id)
            .map((p: any) => p.userId)
        );

        // Count lessons
        const lessonsQuery = query(
          collection(db, "lessons"),
          where("courseId", "==", courseData.id)
        );
        const lessonsSnap = await getDocs(lessonsQuery);
        const lessonCount = lessonsSnap.size;

        // Calculate revenue from actual payments (already filtered for approved payments)
        const courseRevenue = allPayments
          .filter((payment: any) => payment.courseId === courseData.id)
          .reduce((sum: number, payment: any) => sum + (Number(payment.amount) || 0), 0);
        
        totalRevenue += courseRevenue;
        totalStudents += courseStudents.size;
        totalLessons += lessonCount;

        courseStatsList.push({
          id: courseData.id,
          courseId: courseData.courseId,
          title: courseData.title || "ไม่ระบุชื่อ",
          thumbnail: courseData.thumbnail || "",
          price: courseData.price || 0,
          totalStudents: courseStudents.size,
          totalLessons: lessonCount,
          revenue: courseRevenue,
          createdAt: courseData.createdAt?.toDate() || null
        });
      }

      // Sort by revenue (highest first)
      courseStatsList.sort((a, b) => b.revenue - a.revenue);

      setCourses(courseStatsList);
      setTotalStats({
        totalCourses: instructorCourses.length,
        totalStudents,
        totalRevenue,
        totalLessons
      });

      // Load students data
      await loadStudentsData(instructorCourses, allProgress, allPayments);

      setLoading(false);
    } catch (error) {
      console.error("Error loading instructor detail:", error);
      setLoading(false);
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date: Date | null) => {
    if (!date) return "ไม่มีข้อมูล";
    return new Intl.DateTimeFormat('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const calculateStorageCost = (bytes: number) => {
    // Assume cost: 0.015 THB per GB per month
    const gb = bytes / (1024 * 1024 * 1024);
    return gb * 0.015;
  };

  async function loadStudentsData(instructorCourses: any[], allProgress: any[], allPayments: any[]) {
    try {
      const studentsList: StudentInfo[] = [];
      const studentMap = new Map<string, StudentInfo>();

      // Get all unique students from progress
      for (const progress of allProgress) {
        const courseId = progress.courseId;
        const course = instructorCourses.find(c => c.id === courseId);
        if (!course) continue;

        const userId = progress.userId;
        
        // Get user data
        const userDoc = await getDoc(doc(db, "users", userId));
        if (!userDoc.exists()) continue;
        
        const userData = userDoc.data();
        
        // Find payment for this course
        const payment = allPayments.find((p: any) => 
          p.userId === userId && 
          p.courseId === courseId && 
          p.status === "completed"
        );

        // Calculate progress percentage
        const completedLessons = progress.completedLessons?.length || 0;
        const lessonsQuery = query(
          collection(db, "lessons"),
          where("courseId", "==", courseId)
        );
        const lessonsSnap = await getDocs(lessonsQuery);
        const totalLessons = lessonsSnap.size;
        const progressPercent = totalLessons > 0 ? (completedLessons / totalLessons) * 100 : 0;

        const studentKey = `${userId}-${courseId}`;
        if (!studentMap.has(studentKey)) {
          studentMap.set(studentKey, {
            userId,
            name: userData.name || userData.displayName || "ไม่ระบุชื่อ",
            email: userData.email || "",
            courseName: course.title || "ไม่ระบุชื่อคอร์ส",
            courseId,
            amountPaid: payment ? Number(payment.amount) || 0 : 0,
            progress: Math.round(progressPercent),
            lastActivity: progress.lastAccessedAt?.toDate() || null,
            storageUsed: userData.storageUsedBytes || 0,
            enrolledAt: progress.enrolledAt?.toDate() || null,
            banned: userData.banned || false,
          });
        }
      }

      setStudents(Array.from(studentMap.values()));
    } catch (error) {
      console.error("Error loading students data:", error);
    }
  }

  const handleBanStudent = async (userId: string, courseId: string, currentlyBanned: boolean) => {
    if (!user) return;

    const action = currentlyBanned ? "ปลดแบน" : "แบน";
    const confirmed = window.confirm(`คุณต้องการ${action}นักเรียนคนนี้ใช่หรือไม่?`);
    if (!confirmed) return;

    setActionLoading(true);
    try {
      // Update user banned status
      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, {
        banned: !currentlyBanned,
        bannedAt: !currentlyBanned ? new Date() : null,
        bannedBy: !currentlyBanned ? user.uid : null,
      });

      alert(`${action}นักเรียนสำเร็จ`);
      await loadInstructorDetail();
      setSelectedStudent(null);
    } catch (error: any) {
      alert(`เกิดข้อผิดพลาด: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveStudent = async (userId: string, courseId: string) => {
    if (!user) return;

    const confirmed = window.confirm(
      "คุณต้องการลบนักเรียนคนนี้ออกจากคอร์สใช่หรือไม่?\n\nการกระทำนี้จะลบ:\n- ความคืบหน้าในการเรียน\n- ข้อมูลการลงทะเบียน\n\nแต่จะไม่ลบข้อมูลการชำระเงิน"
    );
    if (!confirmed) return;

    setActionLoading(true);
    try {
      // Delete progress
      const progressQuery = query(
        collection(db, "progress"),
        where("userId", "==", userId),
        where("courseId", "==", courseId)
      );
      const progressSnap = await getDocs(progressQuery);
      for (const doc of progressSnap.docs) {
        await deleteDoc(doc.ref);
      }

      alert("ลบนักเรียนสำเร็จ");
      await loadInstructorDetail();
      setSelectedStudent(null);
    } catch (error: any) {
      alert(`เกิดข้อผิดพลาด: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteAllData = async () => {
    if (!instructorId) return;

    setDeleting(true);
    try {
      const functions = getFunctions();
      const deleteInstructorData = httpsCallable(functions, "deleteInstructorData");
      
      const result = await deleteInstructorData({ instructorId });
      
      alert(`ลบข้อมูลสำเร็จ! ${(result.data as any).message}`);
      setShowDeleteConfirm(false);
      
      // Reload data
      await loadInstructorDetail();
    } catch (error: any) {
      console.error("Error deleting instructor data:", error);
      alert(`เกิดข้อผิดพลาด: ${error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">กำลังโหลดข้อมูล...</p>
        </div>
      </div>
    );
  }

  if (!instructor) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl font-bold text-gray-800 mb-4">ไม่พบข้อมูลครู</p>
          <Link to="/admin/system">
            <button className="bg-gradient-to-r from-blue-600 to-pink-600 text-white px-6 py-3 rounded-xl font-semibold">
              กลับไปหน้าแดชบอร์ด
            </button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <Link to="/admin/system">
          <button className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-6 font-semibold">
            <ArrowLeft className="w-5 h-5" />
            กลับไปหน้าแดชบอร์ด
          </button>
        </Link>

        {/* Instructor Profile Card */}
        <div className="bg-white rounded-3xl p-8 shadow-xl mb-8">
          <div className="flex flex-col md:flex-row items-center gap-6">
            {instructor.profileImage ? (
              <img
                src={instructor.profileImage}
                alt={instructor.name}
                className="w-32 h-32 rounded-full object-cover ring-4 ring-purple-200"
              />
            ) : (
              <div className="w-32 h-32 rounded-full bg-gradient-to-br from-blue-600 to-pink-600 flex items-center justify-center text-white text-5xl font-bold">
                {instructor.name?.[0]?.toUpperCase() || "?"}
              </div>
            )}
            <div className="flex-1 text-center md:text-left">
              <h1 className="text-3xl font-bold text-gray-800 mb-2">{instructor.name || "ไม่ระบุชื่อ"}</h1>
              <p className="text-gray-600 mb-2">{instructor.email}</p>
              {instructor.username && (
                <p className="text-blue-600 font-semibold mb-4">@{instructor.username}</p>
              )}
              <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                <span className={`px-4 py-2 rounded-xl text-sm font-semibold ${
                  instructor.subscriptionTier === "yearly"
                    ? "bg-yellow-100 text-yellow-700"
                    : instructor.subscriptionTier === "pro_plus"
                    ? "bg-pink-100 text-pink-700"
                    : instructor.subscriptionTier === "pro"
                    ? "bg-blue-100 text-blue-700"
                    : "bg-gray-100 text-gray-700"
                }`}>
                  <Award className="w-4 h-4 inline mr-2" />
                  {instructor.subscriptionTier?.toUpperCase() || "FREE"}
                </span>
                {instructor.createdAt && (
                  <span className="bg-blue-100 text-blue-700 px-4 py-2 rounded-xl text-sm font-semibold">
                    <Calendar className="w-4 h-4 inline mr-2" />
                    สมัครเมื่อ {formatDate(instructor.createdAt.toDate())}
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {/* Delete All Data Button */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              <Trash2 className="w-5 h-5" />
              ลบข้อมูลทั้งหมด
            </button>
            <p className="text-sm text-gray-500 mt-2">
              ลบคอร์ส บทเรียน รูปโปรไฟล์ และข้อมูลอื่นๆ ทั้งหมดของผู้สอนคนนี้
            </p>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                <BookOpen className="w-7 h-7 text-blue-600" />
              </div>
              <div>
                <p className="text-gray-600 text-sm">คอร์สทั้งหมด</p>
                <p className="text-3xl font-bold text-gray-800">{totalStats.totalCourses}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                <Users className="w-7 h-7 text-blue-600" />
              </div>
              <div>
                <p className="text-gray-600 text-sm">นักเรียนทั้งหมด</p>
                <p className="text-3xl font-bold text-gray-800">{totalStats.totalStudents}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center">
                <DollarSign className="w-7 h-7 text-green-600" />
              </div>
              <div>
                <p className="text-gray-600 text-sm">รายได้รวม</p>
                <p className="text-2xl font-bold text-green-700">{formatCurrency(totalStats.totalRevenue)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-7 h-7 text-blue-600" />
              </div>
              <div>
                <p className="text-gray-600 text-sm">บทเรียนทั้งหมด</p>
                <p className="text-3xl font-bold text-gray-800">{totalStats.totalLessons}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Students List */}
        <div className="bg-white rounded-3xl p-8 shadow-xl mb-8">
          <div className="flex items-center gap-3 mb-6">
            <GraduationCap className="w-8 h-8 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-800">รายชื่อนักเรียน</h2>
            <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-semibold">
              {students.length} คน
            </span>
          </div>

          {students.length === 0 ? (
            <div className="text-center py-12">
              <GraduationCap className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-800 mb-2">ยังไม่มีนักเรียน</h3>
              <p className="text-gray-600">ยังไม่มีนักเรียนลงทะเบียนเรียนในคอร์สของครูคนนี้</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">นักเรียน</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">คอร์ส</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">ชำระเงิน</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">ความคืบหน้า</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">การใช้งาน</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">ต้นทุน</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">สถานะ</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">จัดการ</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {students.map((student, index) => {
                    const storageCost = calculateStorageCost(student.storageUsed);
                    const daysSinceActivity = student.lastActivity 
                      ? Math.floor((new Date().getTime() - student.lastActivity.getTime()) / (1000 * 60 * 60 * 24))
                      : null;

                    return (
                      <tr key={`${student.userId}-${student.courseId}`} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-semibold text-gray-800">{student.name}</p>
                            <p className="text-sm text-gray-500">{student.email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-gray-800">{student.courseName}</p>
                          <p className="text-xs text-gray-500">
                            ลงทะเบียน: {formatDate(student.enrolledAt)}
                          </p>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                            student.amountPaid > 0 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {formatCurrency(student.amountPaid)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-[100px]">
                              <div 
                                className={`h-2 rounded-full ${
                                  student.progress >= 80 ? 'bg-green-500' :
                                  student.progress >= 50 ? 'bg-blue-500' :
                                  student.progress >= 20 ? 'bg-yellow-500' : 'bg-red-500'
                                }`}
                                style={{ width: `${student.progress}%` }}
                              ></div>
                            </div>
                            <span className="text-sm font-semibold text-gray-700">{student.progress}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="space-y-1">
                            <div className="flex items-center gap-1 text-xs text-gray-600">
                              <Clock className="w-3 h-3" />
                              {daysSinceActivity !== null ? (
                                <span>{daysSinceActivity === 0 ? 'วันนี้' : `${daysSinceActivity} วันที่แล้ว`}</span>
                              ) : (
                                <span>ไม่มีข้อมูล</span>
                              )}
                            </div>
                            <div className="flex items-center gap-1 text-xs text-gray-600">
                              <HardDrive className="w-3 h-3" />
                              <span>{formatBytes(student.storageUsed)}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm font-semibold text-blue-600">
                            ฿{storageCost.toFixed(4)}/เดือน
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {student.banned ? (
                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 flex items-center gap-1 w-fit">
                              <UserX className="w-3 h-3" />
                              ถูกแบน
                            </span>
                          ) : (
                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 flex items-center gap-1 w-fit">
                              <CheckCircle className="w-3 h-3" />
                              ปกติ
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => setSelectedStudent(student)}
                            className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm font-semibold hover:bg-blue-200 transition-colors"
                          >
                            จัดการ
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Courses List */}
        <div className="bg-white rounded-3xl p-8 shadow-xl">
          <div className="flex items-center gap-3 mb-6">
            <BookOpen className="w-8 h-8 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-800">คอร์สทั้งหมด</h2>
          </div>

          {courses.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-800 mb-2">ยังไม่มีคอร์ส</h3>
              <p className="text-gray-600">ครูยังไม่ได้สร้างคอร์สใดๆ</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {courses.map((course) => (
                <div
                  key={course.id}
                  className="border-2 border-gray-100 rounded-2xl overflow-hidden hover:border-blue-200 transition-all duration-300"
                >
                  <div className="flex flex-col md:flex-row">
                    {/* Course Thumbnail */}
                    <div className="md:w-64 h-48 md:h-auto">
                      <img
                        src={course.thumbnail || "https://via.placeholder.com/400x300?text=No+Image"}
                        alt={course.title}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Course Info */}
                    <div className="flex-1 p-6">
                      <h3 className="text-xl font-bold text-gray-800 mb-2">{course.title}</h3>
                      {course.courseId && (
                        <p className="text-sm font-mono text-blue-600 mb-4">รหัสคอร์ส: {course.courseId}</p>
                      )}

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div className="bg-blue-50 p-3 rounded-xl">
                          <p className="text-xs text-blue-600 mb-1">ราคา</p>
                          <p className="text-lg font-bold text-blue-700">{formatCurrency(course.price)}</p>
                        </div>

                        <div className="bg-blue-50 p-3 rounded-xl">
                          <p className="text-xs text-blue-600 mb-1">นักเรียน</p>
                          <p className="text-lg font-bold text-blue-700">{course.totalStudents} คน</p>
                        </div>

                        <div className="bg-green-50 p-3 rounded-xl">
                          <p className="text-xs text-green-600 mb-1">รายได้</p>
                          <p className="text-lg font-bold text-green-700">{formatCurrency(course.revenue)}</p>
                        </div>

                        <div className="bg-blue-50 p-3 rounded-xl">
                          <p className="text-xs text-blue-600 mb-1">บทเรียน</p>
                          <p className="text-lg font-bold text-blue-700">{course.totalLessons} บท</p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-gray-600 text-sm">
                          <Calendar className="w-4 h-4" />
                          <span>สร้างเมื่อ {formatDate(course.createdAt)}</span>
                        </div>

                        <div className="flex items-center gap-2">
                          <Link to={`/courses/${course.id}`}>
                            <button className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-4 py-2 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105 flex items-center gap-2">
                              <Eye className="w-4 h-4" />
                              ดูคอร์ส
                            </button>
                          </Link>
                          <Link to={`/instructor/courses/${course.id}/edit?admin=true`}>
                            <button className="bg-gradient-to-r from-blue-600 to-pink-600 text-white px-4 py-2 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105 flex items-center gap-2">
                              <Edit className="w-4 h-4" />
                              แก้ไข
                            </button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Student Management Modal */}
        {selectedStudent && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl p-8 max-w-2xl w-full shadow-2xl">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-800">จัดการนักเรียน</h3>
                <button
                  onClick={() => setSelectedStudent(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <AlertTriangle className="w-6 h-6" />
                </button>
              </div>

              {/* Student Info */}
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-6 mb-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                    {selectedStudent.name[0]?.toUpperCase()}
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-800">{selectedStudent.name}</h4>
                    <p className="text-gray-600">{selectedStudent.email}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-xl p-3">
                    <p className="text-xs text-gray-600 mb-1">คอร์ส</p>
                    <p className="font-semibold text-gray-800">{selectedStudent.courseName}</p>
                  </div>
                  <div className="bg-white rounded-xl p-3">
                    <p className="text-xs text-gray-600 mb-1">ชำระเงิน</p>
                    <p className="font-semibold text-green-700">{formatCurrency(selectedStudent.amountPaid)}</p>
                  </div>
                  <div className="bg-white rounded-xl p-3">
                    <p className="text-xs text-gray-600 mb-1">ความคืบหน้า</p>
                    <p className="font-semibold text-blue-700">{selectedStudent.progress}%</p>
                  </div>
                  <div className="bg-white rounded-xl p-3">
                    <p className="text-xs text-gray-600 mb-1">พื้นที่ใช้งาน</p>
                    <p className="font-semibold text-blue-700">{formatBytes(selectedStudent.storageUsed)}</p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-4">
                {/* Ban/Unban */}
                <div className="border-2 border-gray-200 rounded-2xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Ban className="w-5 h-5 text-red-600" />
                      <h5 className="font-bold text-gray-800">การแบนผู้ใช้</h5>
                    </div>
                    {selectedStudent.banned && (
                      <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold">
                        ถูกแบนอยู่
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    {selectedStudent.banned 
                      ? 'นักเรียนคนนี้ถูกแบนอยู่ ไม่สามารถเข้าสู่ระบบได้'
                      : 'การแบนจะทำให้นักเรียนไม่สามารถเข้าสู่ระบบได้'}
                  </p>
                  <button
                    onClick={() => handleBanStudent(selectedStudent.userId, selectedStudent.courseId, selectedStudent.banned || false)}
                    disabled={actionLoading}
                    className={`w-full py-3 rounded-xl font-semibold transition-all disabled:opacity-50 ${
                      selectedStudent.banned
                        ? 'bg-green-600 hover:bg-green-700 text-white'
                        : 'bg-red-600 hover:bg-red-700 text-white'
                    }`}
                  >
                    {actionLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        กำลังดำเนินการ...
                      </span>
                    ) : selectedStudent.banned ? (
                      <span className="flex items-center justify-center gap-2">
                        <CheckCircle className="w-5 h-5" />
                        ปลดแบนนักเรียน
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <Ban className="w-5 h-5" />
                        แบนนักเรียน
                      </span>
                    )}
                  </button>
                </div>

                {/* Remove from Course */}
                <div className="border-2 border-red-200 rounded-2xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Trash2 className="w-5 h-5 text-red-600" />
                    <h5 className="font-bold text-gray-800">ลบออกจากคอร์ส</h5>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    ลบนักเรียนออกจากคอร์สนี้ (จะลบความคืบหน้าและข้อมูลการลงทะเบียน แต่ไม่ลบข้อมูลการชำระเงิน)
                  </p>
                  <button
                    onClick={() => handleRemoveStudent(selectedStudent.userId, selectedStudent.courseId)}
                    disabled={actionLoading}
                    className="w-full bg-red-600 hover:bg-red-700 text-white py-3 rounded-xl font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {actionLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        กำลังลบ...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-5 h-5" />
                        ลบออกจากคอร์ส
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Close Button */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setSelectedStudent(null)}
                  className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 py-3 rounded-xl font-semibold transition-all"
                >
                  ปิด
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-8 h-8 text-red-600" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">ยืนยันการลบข้อมูล</h3>
                  <p className="text-gray-600 text-sm">การกระทำนี้ไม่สามารถย้อนกลับได้</p>
                </div>
              </div>

              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-6">
                <p className="text-gray-800 font-semibold mb-2">จะลบข้อมูลต่อไปนี้:</p>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• คอร์สทั้งหมด ({totalStats.totalCourses} คอร์ส)</li>
                  <li>• บทเรียนทั้งหมด ({totalStats.totalLessons} บท)</li>
                  <li>• ข้อมูลนักเรียน ({totalStats.totalStudents} คน)</li>
                  <li>• รูปโปรไฟล์และข้อมูลส่วนตัว</li>
                  <li>• ข้อมูล Subscription</li>
                  <li>• ไฟล์มีเดียทั้งหมด</li>
                </ul>
              </div>

              <p className="text-gray-700 mb-6">
                คุณแน่ใจหรือไม่ว่าต้องการลบข้อมูลทั้งหมดของ <span className="font-bold">{instructor.name}</span>?
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-3 rounded-xl font-semibold transition-all disabled:opacity-50"
                >
                  ยกเลิก
                </button>
                <button
                  onClick={handleDeleteAllData}
                  disabled={deleting}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-xl font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {deleting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      กำลังลบ...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-5 h-5" />
                      ยืนยันลบ
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
